/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        HTML5 Interactive Canvas for AI Inpainting & Mask Generation (Task 10.4.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useInpaintingStore } from '../../stores/useInpaintingStore';
import { useTranslation } from '../../i18n';

interface InpaintingCanvasProps {
  className?: string;
}

export const InpaintingCanvas: React.FC<InpaintingCanvasProps> = ({ className = '' }) => {
  const { t } = useTranslation();

  const {
    sourceImage,
    activeTool,
    brushSize,
    brushHardness,
    maskOpacity,
    zoom,
    pan,
    setPan,
    setBinaryMaskDataUrl,
    setHistoryAvailability,
    undoCounter,
    redoCounter,
    clearCounter,
    invertCounter,
  } = useInpaintingStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const imageCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);

  // Dragging and drawing states
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);

  // History stack for Undo/Redo
  const historyRef = useRef<ImageData[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const MAX_HISTORY = 20;

  // Convert current mask to standardized Binary Mask (Black = preserve, White = inpaint)
  const exportBinaryMask = useCallback(() => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;

    const width = maskCanvas.width;
    const height = maskCanvas.height;
    if (width === 0 || height === 0) return;

    const maskCtx = maskCanvas.getContext('2d');
    if (!maskCtx) return;

    const maskData = maskCtx.getImageData(0, 0, width, height);
    const data = maskData.data;

    // Check if mask has any content
    let hasPaintedPixels = false;

    // Create offscreen canvas for binary export
    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = height;
    const offCtx = offscreen.getContext('2d');
    if (!offCtx) return;

    // Fill background with solid black #000000
    offCtx.fillStyle = '#000000';
    offCtx.fillRect(0, 0, width, height);

    const binaryData = offCtx.getImageData(0, 0, width, height);
    const bData = binaryData.data;

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      if (alpha > 20) {
        hasPaintedPixels = true;
        // White = Area to repaint/inpaint
        bData[i] = 255;
        bData[i + 1] = 255;
        bData[i + 2] = 255;
        bData[i + 3] = 255;
      }
    }

    if (hasPaintedPixels) {
      offCtx.putImageData(binaryData, 0, 0);
      setBinaryMaskDataUrl(offscreen.toDataURL('image/png'));
    } else {
      setBinaryMaskDataUrl(null);
    }
  }, [setBinaryMaskDataUrl]);

  // Push current mask state to history stack
  const pushHistorySnapshot = useCallback(() => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;
    const ctx = maskCanvas.getContext('2d');
    if (!ctx) return;

    const snapshot = ctx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);

    // Truncate future branch if user drew after an undo
    const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
    if (newHistory.length >= MAX_HISTORY) {
      newHistory.shift();
    }
    newHistory.push(snapshot);
    historyRef.current = newHistory;
    historyIndexRef.current = newHistory.length - 1;

    setHistoryAvailability(
      historyIndexRef.current > 0,
      historyIndexRef.current < historyRef.current.length - 1
    );

    exportBinaryMask();
  }, [exportBinaryMask, setHistoryAvailability]);

  // Load and render source image onto image canvas
  useEffect(() => {
    if (!sourceImage) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = sourceImage;
    img.onload = () => {
      const imgCanvas = imageCanvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!imgCanvas || !maskCanvas) return;

      const w = img.naturalWidth || 1024;
      const h = img.naturalHeight || 1024;

      imgCanvas.width = w;
      imgCanvas.height = h;
      maskCanvas.width = w;
      maskCanvas.height = h;

      const imgCtx = imgCanvas.getContext('2d');
      if (imgCtx) {
        imgCtx.clearRect(0, 0, w, h);
        imgCtx.drawImage(img, 0, 0, w, h);
      }

      const maskCtx = maskCanvas.getContext('2d');
      if (maskCtx) {
        maskCtx.clearRect(0, 0, w, h);
        // Save initial blank snapshot
        historyRef.current = [maskCtx.getImageData(0, 0, w, h)];
        historyIndexRef.current = 0;
        setHistoryAvailability(false, false);
        setBinaryMaskDataUrl(null);
      }
    };
  }, [sourceImage, setBinaryMaskDataUrl, setHistoryAvailability]);

  // Handle Undo Trigger from Store
  useEffect(() => {
    if (undoCounter === 0) return;
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      const prevData = historyRef.current[historyIndexRef.current];
      const maskCanvas = maskCanvasRef.current;
      if (maskCanvas && prevData) {
        const ctx = maskCanvas.getContext('2d');
        ctx?.putImageData(prevData, 0, 0);
        exportBinaryMask();
        setHistoryAvailability(
          historyIndexRef.current > 0,
          historyIndexRef.current < historyRef.current.length - 1
        );
      }
    }
  }, [undoCounter, exportBinaryMask, setHistoryAvailability]);

  // Handle Redo Trigger from Store
  useEffect(() => {
    if (redoCounter === 0) return;
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      const nextData = historyRef.current[historyIndexRef.current];
      const maskCanvas = maskCanvasRef.current;
      if (maskCanvas && nextData) {
        const ctx = maskCanvas.getContext('2d');
        ctx?.putImageData(nextData, 0, 0);
        exportBinaryMask();
        setHistoryAvailability(
          historyIndexRef.current > 0,
          historyIndexRef.current < historyRef.current.length - 1
        );
      }
    }
  }, [redoCounter, exportBinaryMask, setHistoryAvailability]);

  // Handle Clear Trigger from Store
  useEffect(() => {
    if (clearCounter === 0) return;
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;
    const ctx = maskCanvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      pushHistorySnapshot();
    }
  }, [clearCounter, pushHistorySnapshot]);

  // Handle Invert Trigger from Store
  useEffect(() => {
    if (invertCounter === 0) return;
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;
    const ctx = maskCanvas.getContext('2d');
    if (!ctx) return;

    const w = maskCanvas.width;
    const h = maskCanvas.height;
    const imgData = ctx.getImageData(0, 0, w, h);
    const d = imgData.data;

    for (let i = 0; i < d.length; i += 4) {
      const alpha = d[i + 3];
      if (alpha > 20) {
        // Clear masked pixel to transparent
        d[i + 3] = 0;
      } else {
        // Fill unmasked pixel with mask color
        d[i] = 239;     // Primary / Red mask tint
        d[i + 1] = 68;
        d[i + 2] = 68;
        d[i + 3] = Math.round(maskOpacity * 255);
      }
    }

    ctx.putImageData(imgData, 0, 0);
    pushHistorySnapshot();
  }, [invertCounter, maskOpacity, pushHistorySnapshot]);

  // Translate pointer coordinates to canvas pixel space
  const getCanvasCoords = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  // Drawing event handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (activeTool === 'pan') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      return;
    }

    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const { x, y } = getCanvasCoords(e);

    ctx.beginPath();
    ctx.moveTo(x, y);

    // Configure stroke appearance
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize;

    if (activeTool === 'brush') {
      ctx.globalCompositeOperation = 'source-over';
      // Red mask overlay with user-configured opacity
      ctx.fillStyle = `rgba(239, 68, 68, ${maskOpacity})`;
      ctx.strokeStyle = `rgba(239, 68, 68, ${maskOpacity})`;
      ctx.shadowBlur = brushHardness < 0.9 ? Math.round((1 - brushHardness) * 12) : 0;
      ctx.shadowColor = `rgba(239, 68, 68, ${maskOpacity})`;

      // Dot on click
      ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else if (activeTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      ctx.strokeStyle = 'rgba(0, 0, 0, 1)';

      ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;

    // Track cursor for custom brush indicator
    const rect = canvas.getBoundingClientRect();
    setCursorPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });

    if (isPanning && activeTool === 'pan') {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    if (!isDrawing) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCanvasCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = () => {
    if (isPanning) {
      setIsPanning(false);
    }
    if (isDrawing) {
      setIsDrawing(false);
      pushHistorySnapshot();
    }
  };

  const handlePointerLeave = () => {
    setCursorPos(null);
    if (isPanning) setIsPanning(false);
    if (isDrawing) {
      setIsDrawing(false);
      pushHistorySnapshot();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-[540px] sm:h-[640px] overflow-hidden rounded-2xl bg-surface-elevated/40 border border-surface-border flex items-center justify-center select-none ${className}`}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
    >
      {/* Background Grid Pattern */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(var(--color-foreground) 1px, transparent 1px)',
          backgroundSize: '16px 16px',
        }}
      />

      {sourceImage ? (
        <div
          className="relative origin-center transition-transform duration-75 ease-out shadow-2xl rounded-xl overflow-hidden"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
        >
          {/* Layer 1: Source Image Canvas */}
          <canvas
            ref={imageCanvasRef}
            className="block max-w-none pointer-events-none"
          />

          {/* Layer 2: Interactive Mask Drawing Canvas */}
          <canvas
            ref={maskCanvasRef}
            className={`absolute inset-0 block max-w-none ${
              activeTool === 'pan' ? 'cursor-grab active:cursor-grabbing' : 'cursor-crosshair'
            }`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
          />

          {/* Floating Brush Size Indicator */}
          {cursorPos && activeTool !== 'pan' && (
            <div
              className="pointer-events-none absolute rounded-full border-2 border-primary bg-primary/20 -translate-x-1/2 -translate-y-1/2 transition-[width,height] duration-75"
              style={{
                left: `${cursorPos.x}px`,
                top: `${cursorPos.y}px`,
                width: `${brushSize * zoom}px`,
                height: `${brushSize * zoom}px`,
              }}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center p-8 text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-surface-border/40 flex items-center justify-center text-foreground-muted">
            <svg
              className="w-8 h-8 opacity-60"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="space-y-1 max-w-sm">
            <h4 className="font-heading font-semibold text-base text-foreground">
              {t('inpaint.noImageSelected')}
            </h4>
            <p className="text-xs text-foreground-muted leading-relaxed">
              {t('inpaint.noImageSelectedDesc')}
            </p>
          </div>
        </div>
      )}

      {/* Mode Status Pill Overlay */}
      {sourceImage && (
        <div className="absolute bottom-4 left-4 z-20 pointer-events-none">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface/90 border border-surface-border shadow-md backdrop-blur-md text-[11px] font-medium text-foreground-muted">
            <span
              className={`w-2 h-2 rounded-full ${
                activeTool === 'brush'
                  ? 'bg-rose-500 animate-pulse'
                  : activeTool === 'eraser'
                  ? 'bg-amber-500'
                  : 'bg-primary'
              }`}
            />
            <span>
              {activeTool === 'brush'
                ? t('inpaint.brushModeActive')
                : activeTool === 'eraser'
                ? t('inpaint.eraserModeActive')
                : t('inpaint.panModeActive')}
            </span>
            <span className="text-foreground-muted/60">•</span>
            <span>{Math.round(zoom * 100)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};
